/* eslint-disable @typescript-eslint/no-deprecated */

import * as React from 'react'
import { useFormikContext, useField } from 'formik'
import styled from 'styled-components'
import { ArrowDownward } from '@mui/icons-material'
import { FontWeight, SmallBody } from '../../buildingBlocks/Typography/Text'
import { Select } from '../../styles'
import { WarningIcon } from './WarningIcon'
import { HelperText } from './Field'
import { Required } from './Required'

type InputProps = Omit<React.HTMLProps<HTMLSelectElement>, 'value' | 'onChange' | 'as' | 'ref'>

export interface SelectFieldProps extends InputProps {
  name: string
  label: string
  helperText?: string
  showValidationErrorMessages?: boolean
  required?: boolean
  options: { label: string; value: string }[]
}

export const SelectField: React.FC<SelectFieldProps> = ({
  name,
  label,
  helperText,
  showValidationErrorMessages,
  required = false,
  autoComplete,
  options,
  ...rest
}) => {
  const { setFieldValue, getFieldMeta, submitCount, submitForm } = useFormikContext()
  const [field] = useField(name)

  const { touched, error } = getFieldMeta(name)

  const hasError = touched && error != null && submitCount > 0
  return (
    <Container>
      <Label as="label" htmlFor={name}>
        {label}
        {required && <Required />}
      </Label>
      <StyledInput
        id={name}
        autoComplete={autoComplete}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onChange={(e: any) => setFieldValue(field.name, e.target.value)}
        value={field.value}
        {...rest}
        hasError={hasError}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onKeyDown={(e: any) => (e.keyCode === 13 ? submitForm() : {})}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </StyledInput>
      {(showValidationErrorMessages && hasError) || helperText != null ? (
        <HelperText isError={(hasError && showValidationErrorMessages) ?? false}>
          {hasError && error ? error : helperText}
        </HelperText>
      ) : null}
      {showValidationErrorMessages && hasError ? <WarningIcon indent={4.5} /> : null}
      <StyledDownArrow />
    </Container>
  )
}

const Container = styled.div`
  position: relative;
  margin: 1rem 0;
`
interface StyledInputProps {
  hasError: boolean
}

const StyledDownArrow = styled(ArrowDownward)`
  position: absolute;
  transform: scale(1.5);
  pointer-events: none;
  right: 2rem;
  top: 3.2rem;
`

const StyledInput = styled(Select)<StyledInputProps>`
  box-sizing: border-box;
  width: 100%;
  border-radius: 0.8rem;
  border: 1px solid ${({ hasError, theme }) => (hasError ? theme.colors.danger.dark : theme.colors.grey.light)};
  padding: 1.5rem 1rem;
  margin: 1.2rem 0 0 0;
  color: ${({ theme }) => theme.colors.primary.medium};
  &:active,
  &:focus {
    border: 1px solid ${({ theme, hasError }) => (hasError ? theme.colors.danger.dark : theme.colors.grey.light)};
    outline: none;
  }
  ::placeholder {
    color: ${({ theme }) => theme.colors.grey.light};
  }
`

const Label = styled(SmallBody)`
  margin: 0;
  line-height: 2.4rem;
  font-weight: ${FontWeight.semiBold};
  position: absolute;
  top: 0;
  left: 2.9rem;
  background: linear-gradient(${({ theme }) => theme.colors.white} 70%, transparent 70%);
  pointer-events: none;
  padding: 0 0.2rem;
`
